import { Coordinates } from "./distance.types";

export type Stop = {
  address: string;
  coords: Coordinates;
};

export type Stops = Stop[];

export type Route = Coordinates[];

export type WayCategorySummary = {
  distance_km: number;
  percentage: number;
};

export type WayCategory = {
  summary: Record<string, WayCategorySummary>,
  values: number[][]
}

export type OrsRouteRaw = {
  routes: Array<{
    geometry: string;
    summary: {
      distance: number;
      duration: number;
    };
    extras?: {
      waycategory?: {
        summary: Array<{ value: number; distance: number; amount: number }>;
        values: number[][];
      };
    };
  }>;
};

export type TripResult = { 
  hours: number;
  minutes: number;
  distance_km: number;
  route: Route;
  wayCategory: WayCategory
};

export type CategorisedRoute = {
  coordinates: Route;
  wayCategoryValue: number
}

export const ORS_WAYCATEGORY: Record<number, { name: string; color: string }> = {
  0:  { name: "No category", color: "#999999" },
  1:  { name: "Highway",  color: "#0070f3" },   // motorway
  2:  { name: "Tollway",  color: "#ff4d4d" },   // toll
  4:  { name: "Steps",    color: "#333333" },   // steps
  8:  { name: "Ferry",    color: "#00aaff" },   // ferry / shuttle
  16: { name: "Ford",     color: "#8B4513" },   // ford
};

export function decodeWayCategoryValue(value: number): { name: string; color: string }[] {
  const matches: { name: string; color: string }[] = [];

  // special case
  if (value === 0) {
    matches.push(ORS_WAYCATEGORY[0]);
    return matches;
  }

  for (const bit of Object.keys(ORS_WAYCATEGORY).map(Number)) {
    if (bit === 0) continue;

    if ((value & bit) === bit) {
      matches.push(ORS_WAYCATEGORY[bit]);
    }
  }

  if (matches.length === 0)
    matches.push({ name: `Unknown (${value})`, color: "#888" });

  return matches;
}

export function pickColorForValue(value: number): string {
  if (value === 0) return ORS_WAYCATEGORY[0].color;

  // descend largest → smallest
  const bits = Object.keys(ORS_WAYCATEGORY)
    .map(Number)
    .filter((b) => b !== 0)
    .sort((a, b) => b - a);

  for (const bit of bits) {
    if ((value & bit) === bit) {
      return ORS_WAYCATEGORY[bit].color;
    }
  }

  return "#999"; // fallback
}

export function decodeWayCategorySummary(
  summary: Array<{ value: number; distance: number; amount: number }>
): Record<string, WayCategorySummary> {
  const result: Record<string, WayCategorySummary> = {};

  for (const { value, distance, amount } of summary) {
    if (value === 0) {
      result[ORS_WAYCATEGORY[0].name] = {
        distance_km: distance / 1000,
        percentage: amount,
      };
      continue;
    }

    // For non-zero: break into bits
    for (const bit of Object.keys(ORS_WAYCATEGORY).map(Number)) {
      if (bit === 0) continue;
      if ((value & bit) === bit) {
        const name = ORS_WAYCATEGORY[bit].name ?? `Unknown (${bit})`;
        result[name] = {
          distance_km: distance / 1000,
          percentage: amount,
        };
      }
    }
  }

  return result;
}